import { AlertTitle, Box, Button, LinearProgress, LinearProgressProps, Typography } from '@mui/material';
import { useFormActions, useFormState } from '@ngt/forms-core';
import { FC, useCallback, useContext, useMemo, useState } from 'react';
import { CustomFormField, PostDeleteTemporaryImage, PostUploadTemporaryImageResponse, SpeciesForm } from '../../../../../api/dtos/dtos';
import { ERROR_TIMEOUT } from '../../../../../constants/styles/AlertConstants';
import ServiceStackClientContext from '../../../../../contexts/api/ServiceStackContext';
import SpeciesFormsContext from '../../../../../contexts/data/SpeciesFormsContext';
import AlertContext from '../../../../../contexts/styles/AlertContext';
import FileInput from '../../../../common/FileInput';
import { AppSettings } from '../../../../../AppSettings';
import MapFormContext from '../../../../../contexts/data/MapFormContext';

interface ICustomFileInputProps {
    field: CustomFormField,
    form: SpeciesForm
}

const CustomFileInput: FC<ICustomFileInputProps> = () => {
    //
    // -- Hooks --
    const formActions = useFormActions();
    const { value, errors } = useFormState({ value: true, errors: true });

    //
    // -- Contexts --
    const { selectedForm } = useContext(SpeciesFormsContext);
    const client = useContext(ServiceStackClientContext);
    const { addAlert } = useContext(AlertContext);
    const { setImageGuid } = useContext(MapFormContext);

    //
    // -- States --
    const [file, setFile] = useState<File | null>(null);
    const [progress, setProgress] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [request, setRequest] = useState<XMLHttpRequest | null>(null);

    const showExtraText = useMemo(() => {
        if (value && selectedForm && selectedForm.name) {
            return value[selectedForm.name]?.meta?.hasImage;
        }
        return false;
    }, [value, selectedForm]);

    const processUploadResponse = useCallback((response: XMLHttpRequest) => {
        try {
            const result: PostUploadTemporaryImageResponse = JSON.parse(response.responseText);
            if (response.status === 200) {
                if (!result.responseStatus && result.guid && selectedForm?.name) {
                    setImageGuid(result.guid);
                    formActions.setValue({
                        ...value,
                        [selectedForm.name]: {
                            ...value[selectedForm.name],
                            "guid": result.guid
                        }
                    }, true);
                    setUploading(false);
                    setRequest(null);
                }
                else {
                    setUploading(false);
                    setProgress(0);
                    setFile(null);
                    setRequest(null);
                    addAlert({
                        content: <AlertTitle>
                                    A error occurred while uploading your image.
                                </AlertTitle>,
                        dismissable: true,
                        severity: 'error',
                        timeout: ERROR_TIMEOUT
                    });
                    formActions.setErrors({
                        ...errors,
                        "ImageError": ["Failed to upload image"]
                    });
                    setImageGuid(undefined);
                }
            }
            else {
                setUploading(false);
                setProgress(0);
                setFile(null);
                setRequest(null);
                addAlert({
                    content: <AlertTitle>
                                A error occurred while uploading your image.
                            </AlertTitle>,
                    dismissable: true,
                    severity: 'error',
                    timeout: ERROR_TIMEOUT
                });
                formActions.setErrors({
                    ...errors,
                    "ImageError": ["Failed to upload image"]
                });
                setImageGuid(undefined);
            }
        }
        catch {
            setUploading(false);
            setProgress(0);
            setFile(null);
            setRequest(null);
            addAlert({
                content: <AlertTitle>
                            A error occurred while uploading your image.
                        </AlertTitle>,
                dismissable: true,
                severity: 'error',
                timeout: ERROR_TIMEOUT
            });
            formActions.setErrors({
                ...errors,
                "ImageError": ["Failed to upload image"]
            });
            setImageGuid(undefined);
        }
    }, [setUploading, setProgress, setRequest, value, setFile, addAlert, selectedForm, errors]);

    const uploadFile = useCallback((file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        setFile(file);

        const xhr = new XMLHttpRequest();

        if (xhr.upload) {
            xhr.upload.onprogress = (event) => {
                const done = (event as any).position || event.loaded;
                const total = (event as any).totalSize || event.total;

                const percentage = (Math.floor(done / total * 1000) / 10);

                setProgress(percentage);

                console.log('xhr.upload progress: ' + done + ' / ' + total + ' = ' + (Math.floor(done / total * 1000) / 10) + '%');
            };
        }

        xhr.onreadystatechange = (_) => {
            if (4 === xhr.readyState) {
                processUploadResponse(xhr);
            }
        };
        xhr.open('post', `${AppSettings.baseUrl}/species-forms/temporary-file/upload?format=json`, true);

        xhr.send(formData);

        setRequest(xhr);
        
    }, [setProgress, setRequest, setUploading, processUploadResponse, setFile]);

    const cancelUpload = useCallback(() => {
        request?.abort()
        setRequest(null);
        setUploading(false);
        setProgress(0);
        setFile(null);

        if (selectedForm?.name && value[selectedForm.name].guid) {
            client.delete(new PostDeleteTemporaryImage({ guid: value[selectedForm.name].guid }));
        }
    }, [request, setRequest, setProgress, setUploading, value, client, setFile]);

    const handleFileSelect = useCallback((file?: File) => {
        formActions.setErrors({
            ...errors,
            "ImageError": []
        });

        if (uploading){
            cancelUpload();
        }
        if (file) {
            setUploading(true);
            setProgress(0);
            uploadFile(file);
        }
        else {
            setFile(null);
            setUploading(false);
            setProgress(0);
        }
    }, [uploading, setUploading, setProgress, uploadFile, setFile, errors]);

    function LinearProgressWithLabel(props: LinearProgressProps & { value: number }){
        return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ width: '100%', mr: 1 }}>
                    <LinearProgress variant="determinate" {...props} />
                </Box>
                <Box sx={{ minWidth: 35 }}>
                    <Typography variant="body2" color="text.secondary">
                        {`${Math.round(props.value,)}%`}
                    </Typography>
                </Box>
            </Box>
        );
    }

    const hasError = useMemo(() => {
        if (errors["ImageError"]?.length > 0){ 
            return true;
        }
        return false;
    }, [errors]);
    
    return (
        <>
            {showExtraText && (
                <Typography 
                    sx={{ color: theme => theme.palette.text.warning }}
                >
                    Image already uploaded. Do you want to replace the image?
                </Typography>
            )}
            <FileInput 
                selectedFile={file}
                onFileSelect={handleFileSelect}
                accept="image/*" 
                hasError={hasError}
            />
            {progress > 0 && (
                <LinearProgressWithLabel value={progress} />
            )}
            {file && (
                <Button 
                    variant="text"
                    onClick={() => cancelUpload()}
                    sx={{ width: '100%' }}
                >
                    Clear
                </Button>
            )}
        </>
    )
}

export default CustomFileInput